import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { fetchFeraRating, type FeraRating } from '@/lib/fera'

interface StorefrontState {
  prices:  Record<string, string>
  ratings: Record<string, FeraRating>
}

const StorefrontContext = createContext<StorefrontState>({ prices: {}, ratings: {} })

export function useStorefront() {
  return useContext(StorefrontContext)
}

export function StorefrontProvider({ children }: { children: ReactNode }) {
  const [prices,  setPrices]  = useState<Record<string, string>>({})
  const [ratings, setRatings] = useState<Record<string, FeraRating>>({})

  useEffect(() => {
    function handleMessage(e: MessageEvent) {
      if (!e.data || e.data.type !== 'fitquiz:context') return

      if (e.data.prices && typeof e.data.prices === 'object') {
        setPrices(e.data.prices)
      }

      // Fetch Fera ratings for each product group using Shopify product IDs
      if (e.data.productIds && typeof e.data.productIds === 'object') {
        const ids = e.data.productIds as Record<string, number>
        Object.entries(ids).forEach(([key, shopifyId]) => {
          fetchFeraRating(shopifyId).then(rating => {
            if (!rating) return
            setRatings(prev => ({ ...prev, [key]: rating }))
          })
        })
      }
    }

    window.addEventListener('message', handleMessage)

    // Signal to parent that the quiz is mounted and ready for context
    if (window.parent !== window) {
      try { window.parent.postMessage({ type: 'fitquiz:ready' }, '*') } catch { /* noop */ }
    }

    return () => window.removeEventListener('message', handleMessage)
  }, [])

  return (
    <StorefrontContext.Provider value={{ prices, ratings }}>
      {children}
    </StorefrontContext.Provider>
  )
}
